import { Box, Button, Container, FloatingIndicator, Group, Scroller, Text } from "@mantine/core";
import { useMemo, useState, type FC } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
    LinkPermissionMode,
    type ILayoutItemContext,
    type ILayoutLink,
} from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import styles from "./SubMenu.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import hasEveryPermission from "src/utilities/hasEveryPermission";
import hasAnyPermission from "src/utilities/hasAnyPermission";

interface ISubMenuProps {
    color: string;
    backgroundColor: string;
    indicatorColor: string;
    title?: string;
    menuItems?: ILayoutLink[];
    context?: ILayoutItemContext;
}

interface ISubMenuInternalProps {
    menuItems: ILayoutLink[];
    context?: ILayoutItemContext;
    backgroundColor: string;
    indicatorColor: string;
    color: string;
}

const SubMenuInternal: FC<ISubMenuInternalProps> = ({
    menuItems,
    backgroundColor,
    indicatorColor,
    color,
    context,
}) => {
    const permissions = useCurrentUserPermissions();
    const location = useLocation();

    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLAnchorElement | null>>({});

    const navigation = useMemo(() => {
        const setControlRef = (href: string) => (node: HTMLAnchorElement) => {
            setControlsRefs((current) => {
                const newRefs = { ...current };
                newRefs[href] = node;
                return newRefs;
            });
        };

        const allowedMenuItems = menuItems.filter((menuItem) => {
            const hasPermission =
                menuItem.permissionMode === LinkPermissionMode.All
                    ? hasEveryPermission(permissions, menuItem.permissions, context)
                    : hasAnyPermission(permissions, menuItem.permissions, context);

            return hasPermission;
        });

        const items: React.ReactNode[] = [];

        for (const menuItem of allowedMenuItems) {
            if (menuItem.actionFn) {
                items.push(
                    <Button
                        key={menuItem.href}
                        color={backgroundColor}
                        c={color}
                        radius={0}
                        size="md"
                        ref={setControlRef(menuItem.href) as any}
                        onClick={menuItem.actionFn}
                        leftSection={
                            menuItem.label && menuItem.icon ? (
                                <FontAwesomeIcon icon={menuItem.icon} />
                            ) : undefined
                        }
                        style={menuItem.style}
                        className={menuItem.className}
                    >
                        {menuItem.label ? (
                            menuItem.label
                        ) : menuItem.icon ? (
                            <FontAwesomeIcon icon={menuItem.icon} />
                        ) : null}
                    </Button>,
                );

                continue;
            }

            items.push(
                <Button
                    key={menuItem.href}
                    color={backgroundColor}
                    c={color}
                    radius={0}
                    ref={setControlRef(menuItem.href)}
                    component={RouterLink}
                    to={menuItem.href}
                    target={menuItem.newTab ? "_blank" : undefined}
                    leftSection={
                        menuItem.label && menuItem.icon ? (
                            <FontAwesomeIcon icon={menuItem.icon} />
                        ) : undefined
                    }
                    style={menuItem.style}
                    className={menuItem.className}
                >
                    {menuItem.label ? (
                        menuItem.label
                    ) : menuItem.icon ? (
                        <FontAwesomeIcon icon={menuItem.icon} />
                    ) : null}
                </Button>,
            );
        }

        return {
            items,
            allowedMenuItems,
        };
    }, [menuItems, permissions, setControlsRefs, backgroundColor, context, location.pathname]);

    const selectedHref = useMemo(() => {
        return navigation.allowedMenuItems.find((menuItem) =>
            location.pathname.startsWith(menuItem.href),
        )?.href;
    }, [navigation, location.pathname]);

    return (
        <Scroller
            draggable
            edgeGradientColor={backgroundColor}
            styles={{
                content: {
                    minWidth: "100%",
                },
            }}
        >
            <Group gap={0} ref={setRootRef} pos="relative" wrap="nowrap" w="100%">
                {navigation.items}
                <FloatingIndicator
                    target={controlsRefs[selectedHref]}
                    parent={rootRef}
                    className={styles.indicator}
                    style={{ "--indicator-color": indicatorColor }}
                />
            </Group>
        </Scroller>
    );
};

const SubMenu: FC<ISubMenuProps> = ({
    menuItems,
    color,
    backgroundColor,
    indicatorColor,
    title,
    context,
}) => {
    const hasMenuItems = menuItems && menuItems.length > 0;

    return (
        <Box bg={backgroundColor} c={color}>
            <Container size="xl">
                {title && (
                    <Group justify="center" py={4}>
                        <Text fw="bold">{title}</Text>
                    </Group>
                )}
                {hasMenuItems && (
                    <SubMenuInternal
                        menuItems={menuItems}
                        backgroundColor={backgroundColor}
                        indicatorColor={indicatorColor}
                        color={color}
                        context={context}
                    />
                )}
            </Container>
        </Box>
    );
};

export default SubMenu;
